import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Autocomplete,
  Box,
  Typography,
  Chip,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  MyLocation as MyLocationIcon,
  Clear as ClearIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  History as HistoryIcon,
  BookmarkBorder as SavedIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { addFavorite, addRecent } from '../../../app/redux/slices/addressBookSlice';
import { fetchAddressSuggestions } from '../utils/addressUtils';
import { COLORS } from '../utils/constants';
import useApiCall from '../../../services/api/useApiCall';
import { buildApiConfig } from '../../../services/api/apiClient';
import { endPointEnums } from '../../../services/api/endpoints';

/**
 * Modern address input with autocomplete and saved addresses
 * Now with API persistence for saving addresses
 */
export default function AddressInput({
  label,
  value,
  onChange,
  onUseCurrent,
  showSavedAddresses = true,
}) {
  const dispatch = useDispatch();
  const { makeApiCall } = useApiCall();
  const { recents, favorites } = useSelector((state) => state.addressBook);

  const [inputValue, setInputValue] = useState(typeof value === 'string' ? value : '');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [savedMenuAnchor, setSavedMenuAnchor] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Sync with external value changes
  useEffect(() => {
    if (typeof value === 'string' && value !== inputValue) {
      setInputValue(value);
    }
  }, [value]);

  // Combine saved addresses with search results
  const combinedOptions = useMemo(() => {
    if (!inputValue || inputValue.length < 2) {
      // Show saved addresses when empty or minimal input
      const savedOptions = [];

      if (showSavedAddresses && favorites.length > 0) {
        savedOptions.push({ type: 'header', label: 'Favorites' });
        favorites.slice(0, 3).forEach((addr) => {
          savedOptions.push({
            ...addr,
            type: 'favorite',
            address: addr.address || addr.formatted_address,
          });
        });
      }

      if (showSavedAddresses && recents.length > 0) {
        savedOptions.push({ type: 'header', label: 'Recent' });
        recents.slice(0, 3).forEach((addr) => {
          savedOptions.push({
            ...addr,
            type: 'recent',
            address: addr.address || addr.formatted_address,
          });
        });
      }

      return savedOptions;
    }

    // Add saved addresses that match the search
    const matchingFavorites = favorites
      .filter((addr) => {
        const addrStr = (addr.address || addr.formatted_address || '').toLowerCase();
        return addrStr.includes(inputValue.toLowerCase());
      })
      .map((addr) => ({
        ...addr,
        type: 'favorite',
        address: addr.address || addr.formatted_address,
      }));

    const matchingRecents = recents
      .filter((addr) => {
        const addrStr = (addr.address || addr.formatted_address || '').toLowerCase();
        return addrStr.includes(inputValue.toLowerCase());
      })
      .map((addr) => ({
        ...addr,
        type: 'recent',
        address: addr.address || addr.formatted_address,
      }));

    const saved = [...matchingFavorites, ...matchingRecents];

    // API results
    const apiOptions = options.map((opt) => ({ ...opt, type: 'search' }));

    // Combine: saved first, then API results
    if (saved.length > 0) {
      return [
        { type: 'header', label: 'Saved Addresses' },
        ...saved,
        ...(apiOptions.length > 0
          ? [{ type: 'header', label: 'Search Results' }, ...apiOptions]
          : []),
      ];
    }

    return apiOptions;
  }, [inputValue, options, favorites, recents, showSavedAddresses]);

  // Fetch suggestions with debounce
  useEffect(() => {
    let active = true;
    if (!inputValue || inputValue.length < 2) {
      setOptions([]);
      return undefined;
    }
    setLoading(true);
    const timeout = setTimeout(async () => {
      const results = await fetchAddressSuggestions(inputValue);
      if (active) {
        setOptions(results || []);
        if (results && results.length > 0) setOpen(true);
      }
      setLoading(false);
    }, 350);

    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [inputValue]);

  const handleClear = () => {
    setInputValue('');
    onChange && onChange('');
    setOptions([]);
  };

  const handleSelectAddress = (addressObj) => {
    const val = addressObj.address || addressObj.label || addressObj.formatted_address;
    setInputValue(val);
    onChange && onChange(val, addressObj);
    setOpen(false);

    // Add to recents if not already a favorite
    if (addressObj.type !== 'favorite' && addressObj.type !== 'header') {
      dispatch(
        addRecent({
          address: val,
          formatted_address: val,
          latitude: addressObj.latitude,
          longitude: addressObj.longitude,
        })
      );
    }
  };

  // Save address to API and Redux
  const handleSaveAsFavorite = useCallback(
    async (e) => {
      e.stopPropagation();
      if (!inputValue) return;

      setSaving(true);
      try {
        // Try to save to API first
        const apiConfig = buildApiConfig(
          endPointEnums.CREATE_SAVED_ADDRESS,
          {},
          {
            addressLabel: inputValue.slice(0, 50),
            addressLine1: inputValue,
            addressType: 'PICKUP',
            isFavorite: true,
          }
        );
        await makeApiCall(apiConfig);

        // Also save to Redux for immediate access
        dispatch(
          addFavorite({
            address: inputValue,
            formatted_address: inputValue,
          })
        );

        setSnackbar({
          open: true,
          message: 'Address saved!',
          severity: 'success',
        });
      } catch (error) {
        console.error('Failed to save address to API:', error);
        // Still save locally even if API fails
        dispatch(
          addFavorite({
            address: inputValue,
            formatted_address: inputValue,
          })
        );
        setSnackbar({ open: true, message: 'Saved locally', severity: 'info' });
      } finally {
        setSaving(false);
      }
    },
    [inputValue, dispatch, makeApiCall]
  );

  const handleSavedMenuOpen = (event) => {
    setSavedMenuAnchor(event.currentTarget);
  };

  const handleSavedMenuClose = () => {
    setSavedMenuAnchor(null);
  };

  const handleSelectFromMenu = (addr) => {
    const val = addr.address || addr.formatted_address;
    setInputValue(val);
    onChange && onChange(val, addr);
    handleSavedMenuClose();
  };

  const hasSavedAddresses = favorites.length > 0 || recents.length > 0;

  return (
    <Box sx={{ position: 'relative', backgroundColor: '#fff' }}>
      <Autocomplete
        freeSolo
        open={open && combinedOptions.length > 0}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={combinedOptions}
        getOptionLabel={(opt) => {
          if (typeof opt === 'string') return opt;
          if (opt.type === 'header') return '';
          return opt.address || opt.label || opt.formatted_address || '';
        }}
        getOptionDisabled={(opt) => opt.type === 'header'}
        filterOptions={(x) => x}
        value={inputValue}
        inputValue={inputValue}
        onInputChange={(e, v, reason) => {
          if (reason === 'input') {
            setInputValue(v);
            onChange && onChange(v);
          } else if (reason === 'clear') {
            handleClear();
          }
        }}
        onChange={(e, v) => {
          if (!v) {
            handleClear();
            return;
          }
          if (typeof v === 'string') {
            setInputValue(v);
            onChange && onChange(v);
          } else if (v.type !== 'header') {
            handleSelectAddress(v);
          }
        }}
        loading={loading}
        renderOption={(props, option) => {
          if (option.type === 'header') {
            return (
              <Box
                key={option.label}
                sx={{
                  px: 2,
                  py: 1,
                  backgroundColor: '#f8fafc',
                  borderBottom: `1px solid ${COLORS.border}`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: COLORS.textSecondary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    fontSize: '0.7rem',
                  }}
                >
                  {option.label}
                </Typography>
              </Box>
            );
          }

          return (
            <Box
              component="li"
              {...props}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                mx: 0.5,
                my: 0.25,
                borderRadius: 2,
                cursor: 'pointer',
                backgroundColor: '#fff',
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: '#f1f5f9',
                },
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor:
                    option.type === 'favorite'
                      ? '#fef3c7'
                      : option.type === 'recent'
                        ? '#e0e7ff'
                        : '#f3f4f6',
                  flexShrink: 0,
                }}
              >
                {option.type === 'favorite' ? (
                  <StarIcon sx={{ color: '#f59e0b', fontSize: 18 }} />
                ) : option.type === 'recent' ? (
                  <HistoryIcon sx={{ color: '#4f46e5', fontSize: 18 }} />
                ) : (
                  <LocationIcon sx={{ color: COLORS.primary, fontSize: 18 }} />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: option.type === 'favorite' ? 600 : 500,
                    color: COLORS.textPrimary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.4,
                  }}
                >
                  {option.address || option.label || option.formatted_address}
                </Typography>
              </Box>
              {option.type === 'favorite' && (
                <Chip
                  size="small"
                  label="Saved"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    backgroundColor: '#fef3c7',
                    color: '#92400e',
                  }}
                />
              )}
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            size="small"
            {...params}
            placeholder={label}
            fullWidth
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <LocationIcon sx={{ color: COLORS.textSecondary, fontSize: 18 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {loading && <CircularProgress size={14} sx={{ mr: 1 }} />}
                  {inputValue && (
                    <>
                      <IconButton
                        onClick={handleSaveAsFavorite}
                        size="small"
                        title="Save as favorite"
                        sx={{ color: '#f59e0b', mr: 0.5 }}
                      >
                        <StarBorderIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={handleClear}
                        size="small"
                        sx={{ color: COLORS.textSecondary, mr: 0.5 }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                  {!inputValue && hasSavedAddresses && showSavedAddresses && (
                    <IconButton
                      onClick={handleSavedMenuOpen}
                      size="small"
                      sx={{ color: COLORS.primary, mr: 0.5 }}
                      title="Saved addresses"
                    >
                      <SavedIcon fontSize="small" />
                    </IconButton>
                  )}
                  {params.InputProps.endAdornment}
                  {onUseCurrent && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={onUseCurrent}
                        size="small"
                        sx={{
                          color: COLORS.primary,
                          '&:hover': { backgroundColor: `${COLORS.primary}10` },
                        }}
                      >
                        <MyLocationIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )}
                </>
              ),
            }}
            sx={{
              minWidth: 280,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2.5,
                backgroundColor: COLORS.white,
                fontSize: '0.9rem',
                minHeight: 48,
                '& fieldset': {
                  borderColor: COLORS.border,
                  borderWidth: 1.5,
                },
                '&:hover fieldset': {
                  borderColor: COLORS.primary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: COLORS.primary,
                  borderWidth: 2,
                },
              },
              '& input': {
                fontWeight: 500,
                color: COLORS.textPrimary,
                padding: '12px 14px',
                fontSize: '0.9rem',
              },
            }}
          />
        )}
        ListboxProps={{
          sx: {
            maxHeight: 360,
            p: 0.5,
            '& .MuiAutocomplete-option': {
              p: 0,
              borderRadius: 1.5,
              mx: 0.5,
              my: 0.25,
            },
          },
        }}
        PaperComponent={({ children, ...props }) => (
          <Box
            {...props}
            sx={{
              borderRadius: 3,
              boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
              border: `1px solid ${COLORS.border}`,
              overflow: 'hidden',
              mt: 1,
              backgroundColor: '#ffffff',
              '& .MuiAutocomplete-listbox': {
                backgroundColor: '#ffffff',
                p: 0.5,
              },
            }}
          >
            {children}
          </Box>
        )}
      />

      {/* Saved Addresses Menu */}
      <Menu
        anchorEl={savedMenuAnchor}
        open={Boolean(savedMenuAnchor)}
        onClose={handleSavedMenuClose}
        PaperProps={{
          sx: {
            width: 320,
            maxHeight: 400,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            borderRadius: 2,
          },
        }}
      >
        {favorites.length > 0 && (
          <>
            <Box sx={{ px: 2, py: 1, backgroundColor: COLORS.surface }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: COLORS.textSecondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                ⭐ Favorites
              </Typography>
            </Box>
            {favorites.map((addr, idx) => (
              <MenuItem
                key={`fav-${idx}`}
                onClick={() => handleSelectFromMenu(addr)}
                sx={{ py: 1.5 }}
              >
                <ListItemIcon>
                  <StarIcon sx={{ color: '#f59e0b', fontSize: 18 }} />
                </ListItemIcon>
                <ListItemText
                  primary={addr.address || addr.formatted_address}
                  primaryTypographyProps={{
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    noWrap: true,
                  }}
                />
              </MenuItem>
            ))}
          </>
        )}

        {recents.length > 0 && (
          <>
            {favorites.length > 0 && <Divider />}
            <Box sx={{ px: 2, py: 1, backgroundColor: COLORS.surface }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: COLORS.textSecondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                🕐 Recent
              </Typography>
            </Box>
            {recents.map((addr, idx) => (
              <MenuItem
                key={`rec-${idx}`}
                onClick={() => handleSelectFromMenu(addr)}
                sx={{ py: 1.5 }}
              >
                <ListItemIcon>
                  <HistoryIcon sx={{ color: COLORS.textSecondary, fontSize: 18 }} />
                </ListItemIcon>
                <ListItemText
                  primary={addr.address || addr.formatted_address}
                  primaryTypographyProps={{
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    noWrap: true,
                  }}
                />
              </MenuItem>
            ))}
          </>
        )}
      </Menu>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%', fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
